import { useCallback } from 'react'
import { routes } from '@linqpal/models'
import cookies from '../../../../utils/service/cooker'
import fb from '../../../../utils/service/fb'
import { parsePhoneNumber } from 'libphonenumber-js'
import { SIGN_IN_STEPS, State } from '../../SignIn/types'
import { STEPS } from '../integrationSteps'
import { IInfoResponse } from '@linqpal/models/src/routes2/user/types'

interface IIntegrationActionsParams {
  step: STEPS
  isAuthenticated: boolean
  navigation: any
  setStep: (step: STEPS) => void
  setAuthState: (user: any) => Promise<IInfoResponse | undefined>
  setIsAuthenticated: (state: boolean) => void
  setSignInInitialState: (state: State) => void
  setSignInInitialStep: (step: SIGN_IN_STEPS) => void
  customer: any
  setValue: (field: string, value: any) => void
  setLogin: (email: string) => void
}

interface IIntegrationActionsResponse {
  handleNext: () => void
  handlePayAsGuest: () => void
  handleSignUpUserExists: (authData: State) => void
  handlePermissionDenied: () => Promise<void>
  handleLogin: () => Promise<void>
  handleLogOut: () => Promise<void>
  handleSignUp: () => Promise<void>
  handleAuthChange: () => void
}

export const useIntegrationActions = ({
  step,
  isAuthenticated,
  navigation,
  setStep,
  setAuthState,
  setIsAuthenticated,
  setSignInInitialState,
  setSignInInitialStep,
  customer,
  setValue,
  setLogin,
}: IIntegrationActionsParams): IIntegrationActionsResponse => {
  const handleNext = useCallback(() => {
    if ([STEPS.SIGN_IN, STEPS.SIGN_UP].includes(step) && isAuthenticated) {
      setStep(STEPS.INVOICE_DETAILS)
    }
  }, [step, isAuthenticated, setStep])

  const handlePayAsGuest = useCallback(() => {
    if (step === STEPS.IDLE && !isAuthenticated) {
      navigation.setParams({ isGuest: true })
      setStep(STEPS.PAY_AS_GUEST)
    }
  }, [step, isAuthenticated, navigation, setStep])

  const performLogout = useCallback(async () => {
    console.log('Logging out, clearing session')

    await routes.user.logout()
    await setAuthState(null)
    setIsAuthenticated(false)
    cookies.remove('session')

    await fb.auth().signOut()
    setStep(STEPS.IDLE)
  }, [setAuthState, setIsAuthenticated, setStep])

  const handleLogOut = useCallback(async () => {
    await performLogout()
    setStep(STEPS.IDLE)
  }, [performLogout, setStep])

  const handleSignUpUserExists = useCallback(
    (authData: State) => {
      setSignInInitialState(authData)
      setSignInInitialStep(SIGN_IN_STEPS.SIGN_IN_PASSWORD)
      if (step === STEPS.PAY_AS_GUEST) {
        navigation.setParams({ isGuest: undefined })
      }
      setStep(STEPS.SIGN_IN)
    },
    [step, navigation, setSignInInitialState, setSignInInitialStep, setStep],
  )

  const handlePermissionDenied = useCallback(async () => {
    await performLogout()
    setStep(STEPS.SIGN_IN)
  }, [performLogout, setStep])

  const handleLogin = useCallback(async () => {
    if (!isAuthenticated) {
      if (customer?.email) {
        setSignInInitialState({
          login: customer.email,
          type: 'email',
        })
      }

      if (step === STEPS.IDLE) {
        setStep(STEPS.SIGN_IN)
      }
      if (step === STEPS.PAY_AS_GUEST) {
        navigation.setParams({ isGuest: undefined })
        setStep(STEPS.SIGN_IN)
      }
    }
  }, [
    step,
    isAuthenticated,
    navigation,
    customer,
    setSignInInitialState,
    setStep,
  ])

  const handleSignUpInitialData = useCallback(() => {
    if (customer?.email) {
      setValue('type', 'password')
      setValue('firstName', customer?.firstName ?? '')
      setValue('lastName', customer?.lastName ?? '')
      setValue('email', customer?.email ?? '')
      setValue(
        'phone',
        customer?.phone
          ? parsePhoneNumber(customer.phone, 'US').formatNational()
          : '',
      )
      setLogin(customer?.email)
    }
  }, [customer, setValue, setLogin])

  const handleSignUp = useCallback(async () => {
    if (!isAuthenticated) {
      handleSignUpInitialData()

      if (step === STEPS.IDLE) {
        setStep(STEPS.SIGN_UP)
      }
      if (step === STEPS.PAY_AS_GUEST) {
        navigation.setParams({ isGuest: undefined })
        setStep(STEPS.SIGN_UP)
      }
    }
  }, [step, isAuthenticated, navigation, handleSignUpInitialData, setStep])

  const handleAuthChange = useCallback(() => {
    if (step === STEPS.SIGN_UP) {
      if (customer?.email) {
        setSignInInitialState({
          login: customer.email,
          type: 'email',
        })
      }
      setStep(STEPS.SIGN_IN)
    }
    if (step === STEPS.SIGN_IN) {
      handleSignUpInitialData()
      setStep(STEPS.SIGN_UP)
    }
  }, [step, customer, setSignInInitialState, handleSignUpInitialData, setStep])

  return {
    handleNext,
    handlePayAsGuest,
    handleSignUpUserExists,
    handlePermissionDenied,
    handleLogin,
    handleLogOut,
    handleSignUp,
    handleAuthChange,
  }
}
